import React, { memo, useCallback, useRef, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { MB_LinearGradient } from '@/components/general/linear-gradient';
import { MB_RESOURCE_PICKER_TYPE } from './MB_ResourcePickerUtils';
import { MB_VideoPlayer, MB_VideoPlayerRef } from '@/components/general/video-player';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import { MB_SVG_BrokenFile, MB_SVG_File } from '@/components/general/svg-components';
import { COLORS } from './MB_ResourcePickerConstants';

interface IMB_ResourcePickerAndUploaderPreviewProps {
  /**
   * The uri of the resource to preview.
   */
  resourceUri: string;

  /**
   * The name of the file to preview. This is only used when the resource picker type
   * is `anyFile` and it'll be used to display a text with the filename.
   */
  fileName: string | undefined;

  /**
   * The type of the resource picker to preview.
   */
  resourcePickerType: MB_RESOURCE_PICKER_TYPE;

  /**
   * Upload percentage.
   */
  uploadPercentage: number | undefined;

  /**
   * Whether the resource is loading.
   */
  isLoading: boolean;

  /**
   * The component to display when the user is dragging and dropping a file.
   */
  dragAndDropTextComponent: () => React.JSX.Element;

  /**
   * The color of the preview.
   */
  previewColor?: string;
}

const Preview = memo(
  ({
    resourceUri,
    resourcePickerType,
    fileName,
    color,
  }: {
    /**
     * The uri of the resource to preview.
     */
    resourceUri: string;

    /**
     * The type of the resource picker to preview.
     */
    resourcePickerType: MB_RESOURCE_PICKER_TYPE;

    /**
     * The name of the file to preview. This is only used when the resource picker type
     * is `anyFile` and it'll be used to display a text with the filename.
     */
    fileName: string | undefined;

    /**
     * The color of the preview.
     */
    color?: string;
  }) => {
    const videoRef = useRef<MB_VideoPlayerRef>(null);
    const [isInvisibleVideo, setIsInvisibleVideo] = useState(false);
    const [videoPreviewUnavailable, setVideoPreviewUnavailable] = useState(false);

    const onVideoReady = useCallback(() => {
      setIsInvisibleVideo(videoRef.current?.isInvisibleVideo() ?? false);
    }, []);

    const onVideoError = useCallback(() => {
      setVideoPreviewUnavailable(true);
    }, []);

    if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.image) {
      return <Image style={styles.previewImage} source={{ uri: resourceUri }} />;
    } else if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.video) {
      return (
        <View style={{ width: '100%', height: '100%' }}>
          {isInvisibleVideo && (
            <View style={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', position: 'absolute' }}>
              <MB_SVG_BrokenFile color={color ?? COLORS.white} size={64} />
              <Text style={[styles.videoPreviewUnavailableText, { color }]}>{'Video Preview Unavailable'}</Text>
            </View>
          )}
          <MB_VideoPlayer
            containerStyle={{
              width: '100%',
              height: '100%',
            }}
            sourceUrl={resourceUri}
            showControls={true}
            isLooping
            isMuted
            shouldPlay
            failed={videoPreviewUnavailable}
            MB_Ref={videoRef}
            failedMessage="Video Unavailable"
            onReady={onVideoReady}
            onError={onVideoError}
            isFocused={true}
          />
        </View>
      );
    } else if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.anyFile) {
      // TODO: In the future, we can possibly grab the file type here and show something more meaningful
      return (
        <View style={styles.fileNameContainer}>
          <MB_SVG_File size={32} color={color ?? COLORS.white} />
          <Text style={[styles.fileName, { color }]}>{fileName}</Text>
        </View>
      );
    }

    return null;
  }
);

const MB_ResourcePickerAndUploaderPreview = memo(
  ({ resourceUri, fileName, resourcePickerType, uploadPercentage, isLoading, dragAndDropTextComponent, previewColor }: IMB_ResourcePickerAndUploaderPreviewProps) => {
    return (
      <View style={styles.previewImage}>
        <Preview resourceUri={resourceUri} resourcePickerType={resourcePickerType} fileName={fileName} color={previewColor} />
        <View style={[isLoading ? { height: '100%' } : { top: 0 }, { position: 'absolute', width: '100%', justifyContent: isLoading ? 'center' : 'flex-end' }]}>
          {isLoading ? (
            <MB_LinearGradient
              colors={[mbApplyTransparency(COLORS.plum, 0.21), mbApplyTransparency(COLORS.plum, 0.89)]}
              start={{ x: 1, y: 0 }}
              end={{ x: 0, y: 0 }}
              style={[styles.previewImage, isLoading ? { height: '100%' } : { top: 0 }]}
            >
              <Text style={styles.loadingText}>{uploadPercentage}%</Text>
            </MB_LinearGradient>
          ) : (
            <>{dragAndDropTextComponent()}</>
          )}
        </View>
      </View>
    );
  }
);

export { MB_ResourcePickerAndUploaderPreview };

const styles = StyleSheet.create({
  previewImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    //fontFamily: FONT_STYLES.PoppinsBase,   TODO: Add font family
    fontWeight: '500',
    textAlign: 'center',
    fontSize: 100,
    color: mbApplyTransparency(COLORS.white, 0.25),
  },
  videoPreviewUnavailableText: {
    marginTop: 16,
    //fontFamily: FONT_STYLES.PoppinsBase,  TODO: Add font family
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
  },
  fileNameContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  fileName: {
    //fontFamily: FONT_STYLES.PoppinsBase,   TODO: Add font family
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.white,
  },
});
